// Dialog modal customizado — substitui window.confirm/alert/prompt nativos
// pra padronizar visual com o tema do form/app.
//
// Uso:
//   import { dialog } from '../components/Dialog';
//   const ok = await dialog.confirm('Excluir registro?', { destructive: true });
//   if (ok) doDelete();
//
//   await dialog.alert('Operação concluída.');
//   const name = await dialog.prompt('Qual o nome?', { placeholder: 'Ex: João' });
//
// Implementação: singleton externo (`dialog`) que aciona um state interno do
// <DialogHost/>. Promise resolve quando user clica botão ou fecha.
// DialogHost é mountado uma vez no index.tsx (form e app).

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export interface AlertOptions {
  title?: string;
  okLabel?: string;
}

export interface PromptOptions {
  title?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type DialogState =
  | null
  | { kind: 'confirm'; message: string; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'alert';   message: string; opts: AlertOptions;   resolve: () => void }
  | { kind: 'prompt';  message: string; opts: PromptOptions;  resolve: (v: string | null) => void };

let setStateExternal: ((s: DialogState) => void) | null = null;

export const dialog = {
  confirm(message: string, opts: ConfirmOptions = {}): Promise<boolean> {
    if (!setStateExternal) {
      // Fallback pra window.confirm em SSR ou antes do mount
      return Promise.resolve(typeof window !== 'undefined' ? window.confirm(message) : false);
    }
    return new Promise((resolve) => {
      setStateExternal!({ kind: 'confirm', message, opts, resolve });
    });
  },
  alert(message: string, opts: AlertOptions = {}): Promise<void> {
    if (!setStateExternal) {
      if (typeof window !== 'undefined') window.alert(message);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      setStateExternal!({ kind: 'alert', message, opts, resolve });
    });
  },
  prompt(message: string, opts: PromptOptions = {}): Promise<string | null> {
    if (!setStateExternal) {
      return Promise.resolve(typeof window !== 'undefined' ? window.prompt(message, opts.defaultValue ?? '') : null);
    }
    return new Promise((resolve) => {
      setStateExternal!({ kind: 'prompt', message, opts, resolve });
    });
  },
};

interface DialogHostProps {
  /** Cores opcionais pra herdar do tema do form/app. Default neutros. */
  primaryColor?: string;
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
}

export default function DialogHost({
  primaryColor = '#6366f1',
  textColor = '#1e293b',
  backgroundColor = '#ffffff',
  borderColor = '#e2e8f0',
}: DialogHostProps) {
  const [state, setState] = useState<DialogState>(null);
  const [promptInput, setPromptInput] = useState('');

  useEffect(() => {
    setStateExternal = setState;
    return () => { setStateExternal = null; };
  }, []);

  // Inicializa input do prompt quando state muda
  useEffect(() => {
    if (state?.kind === 'prompt') setPromptInput(state.opts.defaultValue ?? '');
  }, [state]);

  function close() { setState(null); }

  function handleCancel() {
    if (!state) return;
    if (state.kind === 'confirm') state.resolve(false);
    else if (state.kind === 'alert') state.resolve();
    else if (state.kind === 'prompt') state.resolve(null);
    close();
  }

  function handleConfirm() {
    if (!state) return;
    if (state.kind === 'confirm') state.resolve(true);
    else if (state.kind === 'alert') state.resolve();
    else if (state.kind === 'prompt') state.resolve(promptInput);
    close();
  }

  // Esc cancela, Enter confirma (exceto em alert que só fecha).
  // IMPORTANTE: hook ANTES do early return abaixo — React Rules of Hooks.
  useEffect(() => {
    if (!state) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); handleCancel(); }
      else if (e.key === 'Enter' && state && state.kind !== 'prompt') {
        // Em prompt, Enter dentro do input é tratado lá
        e.preventDefault();
        handleConfirm();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, promptInput]);

  // Early return APÓS todos os hooks
  if (!state) return null;

  const title =
    state.opts.title ??
    (state.kind === 'confirm' ? 'Confirmar ação'
      : state.kind === 'alert' ? 'Aviso'
      : 'Entrada');

  const isDestructive = state.kind === 'confirm' && state.opts.destructive;
  const confirmLabel =
    state.kind === 'confirm' ? (state.opts.confirmLabel ?? (isDestructive ? 'Excluir' : 'Confirmar'))
    : state.kind === 'alert' ? (state.opts.okLabel ?? 'OK')
    : (state.opts.confirmLabel ?? 'OK');
  const cancelLabel =
    state.kind === 'confirm' ? (state.opts.cancelLabel ?? 'Cancelar')
    : state.kind === 'prompt' ? (state.opts.cancelLabel ?? 'Cancelar')
    : null;

  const confirmBg = isDestructive ? '#dc2626' : primaryColor;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="nanoapp-dialog-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.45)',
        padding: 16,
        animation: 'nanoapp-dialog-fadein 120ms ease-out',
      }}
    >
      <style>{`
        @keyframes nanoapp-dialog-fadein {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes nanoapp-dialog-pop {
          from { opacity: 0; transform: scale(0.96) translateY(4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
      <div
        style={{
          background: backgroundColor,
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          maxWidth: 440,
          width: '100%',
          border: `1px solid ${borderColor}`,
          animation: 'nanoapp-dialog-pop 140ms ease-out',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${borderColor}` }}>
          <h3 id="nanoapp-dialog-title" style={{ margin: 0, fontSize: 14, fontWeight: 600, color: textColor }}>
            {title}
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Fechar"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 6, border: 'none',
              background: 'transparent', color: textColor, opacity: 0.5, cursor: 'pointer',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.05)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: '16px 20px', color: textColor, fontSize: 14, lineHeight: 1.5 }}>
          {state.message}
          {state.kind === 'prompt' && (
            <input
              type="text"
              autoFocus
              value={promptInput}
              onChange={(e) => setPromptInput((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
              placeholder={state.opts.placeholder}
              style={{
                marginTop: 12, width: '100%',
                padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${borderColor}`,
                fontSize: 14, color: textColor, background: backgroundColor,
                outline: 'none',
              }}
            />
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px 16px' }}>
          {cancelLabel && (
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${borderColor}`,
                background: 'transparent', color: textColor, fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            autoFocus={state.kind === 'alert'}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: confirmBg, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
